import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Dropdown, Tab } from 'react-bootstrap';
import { useHistory, Link } from 'react-router-dom';
import { FaPlus, FaArrowLeft, FaSearch, FaFilter, FaTrash, FaEdit } from 'react-icons/fa';
import { FaPrint } from 'react-icons/fa';
import swal from 'sweetalert';
import Loader from './LoaderModal';
import Side from './Sidebar';
import Top from './Topbar';
import C from '../lib/Config';
import '../App.css';

const Invoice = () => {
  const history = useHistory();
  
  // State initialization
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nameSearchTerm, setNameSearchTerm] = useState('');
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState('invoices');
  const [patientSearch, setPatientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [loaderModal, setLoaderModal] = useState(false);
  const [totalDiscounts, setTotalDiscounts] = useState({});
  // Get authentication headers
  const getAuthHeaders = () => {
    return {
      'Content-Type': 'application/json'
    };
  };

  // Fetch all invoices when component mounts
  useEffect(() => {
    fetchAllInvoices();
  }, []);

  // Fetch all invoices
  const fetchAllInvoices = async () => {
    setLoading(true);
    setLoaderModal(true);

    try {
      // Get doctorId from localStorage (userId is doctorId)
      const doctorId = localStorage.getItem("userId");
      console.log('Fetching invoices for doctorId:', doctorId);
      
      const response = await fetch(`${C.getUrl()}/invoice/getAllInvoices`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }

      const data = await response.json();
      
      // Check if there's a dataBody that needs parsing
      let invoicesData = [];
      if (data.statusCode === 200 && data.message) {
        try {
          invoicesData = JSON.parse(data.message);
        } catch (parseError) {
          console.error('Error parsing invoices:', parseError);
          invoicesData = [];
        }
      } else if (Array.isArray(data)) {
        invoicesData = data;
      }
      
      // Filter invoices by doctorId if available
      if (doctorId) {
        console.log('Filtering invoices by doctorId:', doctorId);
        // Check for createdBy, updatedBy, or doctor fields that might contain doctorId
        invoicesData = invoicesData.filter(invoice => {
          return (
            (invoice.createdBy && invoice.createdBy.toString() === doctorId) ||
            (invoice.updatedBy && invoice.updatedBy.toString() === doctorId) ||
            (invoice.doctor && invoice.doctor.toString() === doctorId)
          );
        });
        console.log('Filtered invoices count:', invoicesData.length);
      }
      
      // Sort invoices by updateTime or createTime (newest first)
      invoicesData.sort((a, b) => {
        // First try to sort by updateTime/updateDate if available
        const aUpdateTime = a.updateTime || a.updateDate;
        const bUpdateTime = b.updateTime || b.updateDate;
        
        // If both have update times, use those for comparison
        if (aUpdateTime && bUpdateTime) {
          return new Date(bUpdateTime) - new Date(aUpdateTime); // Descending order
        }
        
        // Otherwise fall back to creation time
        const aCreateTime = a.createTime || a.createdDate;
        const bCreateTime = b.createTime || b.createdDate;
        
        if (aCreateTime && bCreateTime) {
          return new Date(bCreateTime) - new Date(aCreateTime); // Descending order
        }
        
        // If no timestamps available, use invoice date as fallback
        return new Date(b.invoiceDate) - new Date(a.invoiceDate);
      });

      setInvoices(invoicesData);
      setSearchResults(invoicesData);
      setTotalInvoices(invoicesData.length);
      
      console.log('Invoices fetched successfully:', invoicesData);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.toString());
      swal({ 
        title: "Error!", 
        text: "Failed to fetch invoices: " + err, 
        icon: "error", 
        buttons: false, 
        timer: 2000 
      });
    } finally {
      setLoading(false);
      setLoaderModal(false);
    }
  };


  const handlePrint = async (invoice) => {
    try {
      // Fetch items for the specific invoice
      const itemsResponse = await fetch(`${C.getUrl()}/invoice/item/getByInvoiceNumber/${invoice.invoiceNumber}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
  
      if (!itemsResponse.ok) {
        throw new Error('Failed to fetch invoice items');
      }
  
      const itemsData = await itemsResponse.json();
      
      // Parse items from the response
      let invoiceItems = [];
      if (itemsData.statusCode === 200 && itemsData.message) {
        try {
          const parsedData = JSON.parse(itemsData.message);
          invoiceItems = parsedData.itemList || [];
        } catch (parseError) {
          console.error('Error parsing invoice items:', parseError);
        }
      }
  
      // Calculate the total of all individual item discounts
      let itemDiscountsTotal = 0;
      
      // First calculate the base total amount (without any discounts)
      const baseTotal = invoiceItems.reduce((total, item) => 
        total + ((item.costPerUnit || 0) * (item.quantity || 1)), 0);
  
      // Calculate individual item discounts
      invoiceItems.forEach(item => {
        const price = item.costPerUnit || 0;
        const quantity = item.quantity || 1;
        const itemTotal = price * quantity;
        
        // If the item has an explicit itemTotalDis field, use it
        if (item.itemTotalDis !== undefined && !isNaN(parseFloat(item.itemTotalDis))) {
          itemDiscountsTotal += parseFloat(item.itemTotalDis);
          console.log('Item total discount:', item.itemTotalDis);
        }
        // Otherwise check for percentage discount
        else if (item.itemPType === "%" && item.itemP) {
          const discountAmount = (itemTotal * item.itemP) / 100;
          itemDiscountsTotal += discountAmount;
        } 
        // Check for fixed amount discount
        else if (item.itemRType === "₹" && item.itemR) {
          itemDiscountsTotal += parseFloat(item.itemR);
        }
        // Fall back to standard discount field
        else if (item.discount) {
          // If there's no type specified, assume it's a percentage
          itemDiscountsTotal += (itemTotal * parseFloat(item.discount)) / 100;
        }
      });
  
      // Calculate global discount
      let globalDiscountAmount = 0;
      
      // Check if there's a percentage-based global discount
      if (invoice.invoicePType === "Percentage(%)" && invoice.Dpercentag) {
        // Convert percentage to amount
        globalDiscountAmount = (baseTotal * parseFloat(invoice.Dpercentag)) / 100;
      }
      // Check for fixed amount global discount
      else if (invoice.invoiceRType === "Fixed amount" && invoice.invoiceR) {
        globalDiscountAmount = parseFloat(invoice.invoiceR);
      }
      // Fall back to the standard discount field
      else if (invoice.discount) {
        globalDiscountAmount = parseFloat(invoice.discount);
      }
  
      // Store both individual and global discount components
      const totalItemDiscounts = itemDiscountsTotal;
      const totalGlobalDiscount = globalDiscountAmount;
      
      // Calculate combined total discount
      const totalCombinedDiscount = totalItemDiscounts + totalGlobalDiscount;
      
      // Store the calculated discount values for this invoice
      setTotalDiscounts(prev => ({
        ...prev,
        [invoice.invoiceNumber]: {
          itemDiscount: totalItemDiscounts,
          globalDiscount: totalGlobalDiscount,
          totalDiscount: totalCombinedDiscount
        }
      }));
  
      // Fetch invoice payments
      const paymentsResponse = await fetch(`${C.getUrl()}/invoice/payment/getByInvoiceNumber/${invoice.invoiceNumber}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
  
      let invoicePayments = [];
      if (paymentsResponse.ok) {
        const paymentsData = await paymentsResponse.json();
        if (paymentsData.statusCode === 200 && paymentsData.message) {
          try {
            const parsedPaymentsData = JSON.parse(paymentsData.message);
            invoicePayments = parsedPaymentsData.paymentList || [];
          } catch (parseError) {
            console.error('Error parsing invoice payments:', parseError);
          }
        }
      }
  
      // Fetch institute details if patient ID is available
      let instituteDetails = null;
      if (invoice.patientId) {
        try {
          // Get patient details to extract institute information
          const patientResponse = await fetch(`${C.getUrl()}/userCase/getPatientDetail/${invoice.patientId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });
  
          if (patientResponse.ok) {
            const patientData = await patientResponse.json();
            
            if (patientData.statusCode === 200 && patientData.message) {
              const parsedPatientData = JSON.parse(patientData.message);
              console.log('Patient data in invoice print:', parsedPatientData);
              
              // Get institute ID from patient data
              if (parsedPatientData.instituteDto) {
                const instituteInfo = parsedPatientData.instituteDto;
                const instituteId = instituteInfo.instituteId;
                
                if (instituteId) {
                  try {
                    // Fetch detailed institute information directly using getInstituteDetailsById endpoint
                    const instituteResponse = await fetch(`${C.getUrl()}/institute/getInstituteDetailsById/${instituteId}`, {
                      method: 'GET',
                      headers: { 'Content-Type': 'application/json' }
                    });
                    
                    if (instituteResponse.ok) {
                      const instituteData = await instituteResponse.json();
                      
                      if (instituteData.statusCode === 200 && instituteData.message) {
                        const parsedInstituteData = JSON.parse(instituteData.message);
                        console.log('Detailed institute data in print function:', parsedInstituteData);
                        
                        // Construct full address using all available address components
                        const addressParts = [
                          parsedInstituteData.addrLine1,
                          parsedInstituteData.addrLine2,
                          parsedInstituteData.cityMstDTO?.cityName,
                          parsedInstituteData.stateMstDTO?.stateName,
                          parsedInstituteData.zipPinCode
                        ].filter(Boolean);
                    
                        const fullAddress = addressParts.join(', ');
                        
                        // Create institute details object with comprehensive information
                        instituteDetails = {
                          instituteName: parsedInstituteData.instituteName || instituteInfo.instituteName || 'Healthcare Facility',
                          address: fullAddress || '',
                          city: parsedInstituteData.cityMstDTO?.cityName || '',
                          state: parsedInstituteData.stateMstDTO?.stateName || '',
                          pinCode: parsedInstituteData.zipPinCode || '',
                          contactNumber: parsedInstituteData.contactNumber || '',
                          email: parsedInstituteData.emailId || ''
                        };
                      } else {
                        throw new Error('No institute details in response');
                      }
                    } else {
                      throw new Error('Failed to fetch institute details');
                    }
                  } catch (error) {
                    console.error('Error fetching detailed institute info:', error);
                    // Fallback to basic institute info from patient data if API call fails
                    const fallbackAddressParts = [
                      parsedPatientData.addressLine1,
                      parsedPatientData.addressLine2,
                      parsedPatientData.cityCode,
                      parsedPatientData.stateName,
                      parsedPatientData.pinCode
                    ].filter(Boolean);
                    
                    const fallbackAddress = fallbackAddressParts.join(', ');
                    
                    instituteDetails = {
                      instituteName: instituteInfo.instituteName || 'Healthcare Facility',
                      address: fallbackAddress || '',
                      contactNumber: parsedPatientData.phoneNumber1 || '',
                      email: parsedPatientData.emailId || ''
                    };
                  }
                } else {
                  // Fallback if no institute ID is available
                  const addressParts = [
                    parsedPatientData.addressLine1,
                    parsedPatientData.addressLine2,
                    parsedPatientData.cityCode,
                    parsedPatientData.stateName,
                    parsedPatientData.pinCode
                  ].filter(Boolean);
                  
                  const address = addressParts.join(', ');
                  
                  instituteDetails = {
                    instituteName: instituteInfo.instituteName || 'Healthcare Facility',
                    address: address || '',
                    contactNumber: parsedPatientData.phoneNumber1 || '',
                    email: parsedPatientData.emailId || ''
                  };
                }
              }
            }
          }
        } catch (error) {
          console.error('Error fetching institute details:', error);
          // Continue with printing even if institute fetch fails
        }
      }
  
      // Calculate financial values
      const netAmount = invoiceItems.reduce((total, item) => 
        total + ((item.costPerUnit || 0) * (item.quantity || 1)), 0);
      const received = invoice.received || 0;
      const tax = 0; // No tax in the current implementation
      const subtotal = netAmount - totalCombinedDiscount - tax;
      const due = netAmount - received - totalCombinedDiscount;
  
      // Format date function
      const formatDateForPrint = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      };
  
      // Get institute information
      const instituteName = instituteDetails?.instituteName || 'Healthcare Facility';
      const instituteAddress = instituteDetails?.address || 'Address not available';
      const instituteContact = instituteDetails?.contactNumber || '';
      const instituteEmail = instituteDetails?.email || '';
  
      // Convert amount to words (same function as in ViewInvoice)
      const convertAmountToWords = (amount) => {
        const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
        const convertLessThanThousand = (n) => {
          if (n === 0) return '';
          
          let words = '';
          if (n >= 100) {
            words += units[Math.floor(n / 100)] + ' Hundred ';
            n %= 100;
          }
          
          if (n >= 20) {
            words += tens[Math.floor(n / 10)] + ' ';
            n %= 10;
          }
          
          if (n >= 10) {
            words += teens[n - 10] + ' ';
          } else if (n > 0) {
            words += units[n] + ' ';
          }
          
          return words;
        };
  
        const amountInt = Math.floor(amount);
        const amountPaise = Math.round((amount - amountInt) * 100);
  
        let words = '';
        const crore = Math.floor(amountInt / 10000000);
        const lakhs = Math.floor((amountInt % 10000000) / 100000);
        const thousands = Math.floor((amountInt % 100000) / 1000);
        const hundreds = amountInt % 1000;
  
        if (crore > 0) words += convertLessThanThousand(crore) + 'Crore ';
        if (lakhs > 0) words += convertLessThanThousand(lakhs) + 'Lakh ';
        if (thousands > 0) words += convertLessThanThousand(thousands) + 'Thousand ';
        if (hundreds > 0) words += convertLessThanThousand(hundreds);
  
        words = words.trim() + ' Rupees';
        
        if (amountPaise > 0) {
          words += ' and ' + convertLessThanThousand(amountPaise) + 'Paise';
        }
  
        return words + ' Only';
      };
  
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      // Generate HTML content for printing with detailed discount breakdown
      const printContent = `
        <html>
          <head>
            <title>Invoice - ${invoice.invoiceNumber}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
              .section { margin-bottom: 15px; }
              .section-title { font-weight: bold; color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 5px; }
              table { width: 100%; margin-top: 10px; border-collapse: separate; border-spacing: 0; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; position: relative; }
              th { background-color: #f2f2f2; border-top: 2px solid #999; border-bottom: 2px solid #ddd; }
              thead tr { border-top: 2px solid #999; }
              td { border: 1px solid #ddd; }
              .totals { display: flex; justify-content: flex-end; margin-top: 20px; }
              .totals-table { width: 300px; }
              .amount-in-words { margin-top: 20px; font-style: italic; }
              .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
              .info-box { border: 1px solid #ddd; padding: 15px; border-radius: 5px; background-color: #f9f9f9; }
              .info-box h3 { margin-top: 0; color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 8px; }
              .info-box p { margin-bottom: 5px; }
              .institute-header { text-align: center; margin-bottom: 15px; }
              .institute-header h2 { margin-bottom: 5px; color: #2c3e50; }
              .institute-header p { margin: 0; color: #555; }
              .discount-breakdown { margin-top: 10px; font-size: 0.9em; color: #555; }
            </style>
          </head>
          <body>
            <div class="institute-header">
              <h2>${instituteName}</h2>
              <p>${instituteAddress}</p>
              ${instituteContact ? `<p>Contact: ${instituteContact}</p>` : ''}
              ${instituteEmail ? `<p>Email: ${instituteEmail}</p>` : ''}
            </div>
            
            <div style="text-align: center; margin-bottom: 20px;">
              <h1 style="color: #333;">INVOICE</h1>
            </div>
            
            <div class="info-grid">
              <div class="info-box">
                <h3>Invoice Details</h3>
                <p><strong>Invoice Number:</strong> ${invoice.invoiceNumber}</p>
                <p><strong>Invoice Date:</strong> ${formatDateForPrint(invoice.invoiceDate)}</p>
                ${invoice.department === 'IPD' ? 
                  `<p><strong>Admission Date:</strong> ${formatDateForPrint(invoice.admission_Date) || 'N/A'}</p>
                  <p><strong>Discharge Date:</strong> ${formatDateForPrint(invoice.discharge_Date) || 'N/A'}</p>` : 
                  `<p><strong>Visit Date:</strong> ${formatDateForPrint(invoice.visitDate) || 'N/A'}</p>`
                }
                <p><strong>Department:</strong> ${invoice.department || 'N/A'}</p>
              </div>
              <div class="info-box">
                <h3>Patient Information</h3>
                <p><strong>Name:</strong> ${invoice.patientName}</p>
                <p><strong>Patient ID:</strong> ${invoice.patientId || 'N/A'}</p>
                <p><strong>Phone Number:</strong> ${invoice.phoneNumber || invoice.mobileNumber || 'N/A'}</p>
                <p><strong>Primary Consultant:</strong> ${invoice.primaryConsultant || invoice.doctor || 'N/A'}</p>
              </div>
            </div>
            
            <div class="section">
              <h3 class="section-title">Invoice Items</h3>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Cost Per Unit</th>
                    <th>Quantity</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${invoiceItems.map(item => `
                    <tr>
                      <td>${item.item || 'N/A'}</td>
                      <td>${item.category || 'N/A'}</td>
                      <td>₹${(item.costPerUnit || 0).toFixed(2)}</td>
                      <td>${item.quantity || 1}</td>
                      <td>₹${((item.costPerUnit || 0) * (item.quantity || 1)).toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="totals">
              <table class="totals-table">
                <tr>
                  <th>Net Amount</th>
                  <td>₹${netAmount.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Discount</th>
                  <td>
                    ₹${totalCombinedDiscount.toFixed(2)}
                    <div class="discount-breakdown">
                      ${totalItemDiscounts > 0 ? `Item Discounts: ₹${totalItemDiscounts.toFixed(2)}<br>` : ''}
                      ${totalGlobalDiscount > 0 ? `Global Discount: ₹${totalGlobalDiscount.toFixed(2)}` : ''}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>Tax</th>
                  <td>₹${tax.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Subtotal</th>
                  <td>₹${subtotal.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Amount Received</th>
                  <td>₹${received.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Due Amount</th>
                  <td>₹${due.toFixed(2)}</td>
                </tr>
              </table>
            </div>
            
            <div class="amount-in-words">
              <p><strong>Amount in words:</strong> ${convertAmountToWords(subtotal)}</p>
            </div>
          </body>
        </html>
      `;
      
      // Write the content to the print window
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = function() {
        printWindow.print();
      };
  
    } catch (error) {
      console.error('Error fetching or printing invoice:', error);
      swal({
        title: "Error!",
        text: "Failed to fetch invoice items for printing",
        icon: "error"
      });
    }
  };

  // Search invoice by invoice number
  const searchByInvoiceNumber = async (invoiceNumber) => {
    if (!invoiceNumber) {
      fetchAllInvoices();
      return;
    }
    
    setLoaderModal(true);
    
    try {
      const response = await fetch(`${C.getUrl()}/invoice/getByInvoiceNumber/${invoiceNumber}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to search invoice');
      }

      const data = await response.json();
      
      // Handle different response formats
      let invoiceData = [];
      if (data.statusCode === 200 && data.message) {
        try {
          invoiceData = JSON.parse(data.message);
          // Ensure it's an array
          invoiceData = Array.isArray(invoiceData) ? invoiceData : [invoiceData];
        } catch (parseError) {
          console.error('Error parsing invoice data:', parseError);
          invoiceData = [];
        }
      }

      if (invoiceData.length > 0) {
        setSearchResults(invoiceData);
        console.log('Invoice found:', invoiceData);
      } else {
        setSearchResults([]);
        swal({ 
          title: "Information", 
          text: "No invoice found with that number", 
          icon: "info", 
          buttons: false, 
          timer: 2000 
        });
      }
    } catch (err) {
      console.error('Error searching invoice by number:', err);
      setError(err.toString());
      swal({ 
        title: "Error!", 
        text: "Search failed: " + err, 
        icon: "error", 
        buttons: false, 
        timer: 2000 
      });
    } finally {
      setLoaderModal(false);
    }
  };

  // Search invoices by patient ID
  const searchByPatientId = async (patientId) => {
    if (!patientId) {
      fetchAllInvoices();
      return;
    }
    
    setLoaderModal(true);
    
    try {
      const response = await fetch(`${C.getUrl()}/invoice/getByPatientId/${patientId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to search invoices by patient ID');
      }

      const data = await response.json();
      
      // Handle different response formats
      let invoiceData = [];
      if (data.statusCode === 200) {
        try {
          const parsedData = JSON.parse(data.message);
          invoiceData = parsedData.stagingListl || [];
        } catch (parseError) {
          console.error('Error parsing invoice data:', parseError);
          invoiceData = [];
        }
      }

      if (invoiceData.length > 0) {
        setSearchResults(invoiceData);
        console.log('Patient invoices found:', invoiceData);
      } else {
        setSearchResults([]);
        swal({ 
          title: "Information", 
          text: "No invoices found for that patient ID", 
          icon: "info", 
          buttons: false, 
          timer: 2000 
        });
      }
    } catch (err) {
      console.error('Error searching invoices by patient ID:', err);
      setError(err.toString());
      swal({ 
        title: "Error!", 
        text: "Search failed: " + err, 
        icon: "error", 
        buttons: false, 
        timer: 2000 
      });
    } finally {
      setLoaderModal(false);
    }
  };

  // Search invoices by patient name
  const searchByPatientName = async (patientName) => {
    if (!patientName) {
      fetchAllInvoices();
      return;
    }
    
    setLoaderModal(true);
    
    try {
      const response = await fetch(`${C.getUrl()}/invoice/getByPatientName?patientName=${encodeURIComponent(patientName)}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to search invoices by patient name');
      }

      const data = await response.json();
      
      // Handle different response formats
      let invoiceData = [];
      if (data.statusCode === 200) {
        try {
          const parsedData = JSON.parse(data.message);
          invoiceData = parsedData.invoices || parsedData || [];
        } catch (parseError) {
          console.error('Error parsing invoice data:', parseError);
          invoiceData = [];
        }
      }

      if (invoiceData.length > 0) {
        setSearchResults(invoiceData);
        console.log('Patient invoices found:', invoiceData);
      } else {
        setSearchResults([]);
        swal({ 
          title: "Information", 
          text: "No invoices found for that patient name", 
          icon: "info", 
          buttons: false, 
          timer: 2000 
        });
      }
    } catch (err) {
      console.error('Error searching invoices by patient name:', err);
      setError(err.toString());
      swal({ 
        title: "Error!", 
        text: "Search failed: " + err, 
        icon: "error", 
        buttons: false, 
        timer: 2000 
      });
    } finally {
      setLoaderModal(false);
    }
  };

  // Handle search based on patient name or ID
  const handleSearch = () => {
    if (patientSearch) {
      // Check if the search term is numeric (likely to be a patient ID)
      if (/^\d+$/.test(patientSearch)) {
        searchByPatientId(patientSearch);
      } else {
        // Assume it's a name
        searchByPatientName(patientSearch);
      }
    } else {
      fetchAllInvoices();
    }
  };

  // Handle search by invoice number
  const handleInvoiceNumberSearch = () => {
    if (invoiceNumberFilter) {
      searchByInvoiceNumber(invoiceNumberFilter);
    } else {
      fetchAllInvoices();
    }
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      applyFilters();
    }
  };


  const applyFilters = () => {
    setLoaderModal(true);
    
    // If all filters are empty, just fetch all invoices
    if (!nameSearchTerm && !invoiceNumberFilter && !statusFilter) {
      fetchAllInvoices();
      return;
    }
    
    // Start with the full list of invoices and apply filters locally
    let filtered = [...invoices];
    
    // Apply name/UHID/Mobile filter if provided
    if (nameSearchTerm) {
      filtered = filtered.filter(invoice => {
        const patientNameMatch = invoice.patientName && 
          invoice.patientName.toLowerCase().includes(nameSearchTerm.toLowerCase());
        const uhidMatch = invoice.patientId && 
          invoice.patientId.toString().includes(nameSearchTerm);
        const mobileMatch = invoice.mobileNumber && 
          invoice.mobileNumber.includes(nameSearchTerm);
        
        return patientNameMatch || uhidMatch || mobileMatch;
      });
    }
    
    // Apply invoice number filter if provided
    if (invoiceNumberFilter) {
      filtered = filtered.filter(invoice => 
        invoice.invoiceNumber && 
        invoice.invoiceNumber.includes(invoiceNumberFilter)
      );
    }
    
    // Apply status filter if provided
    if (statusFilter) {
      filtered = filtered.filter(invoice => {
        // Case-insensitive comparison for status
        // Also handle various status format possibilities
        const invoiceStatus = (invoice.status || '').toLowerCase();
        const filterStatus = statusFilter.toLowerCase();
        
        // Check for exact match or partial match
        return invoiceStatus === filterStatus || 
               invoiceStatus.includes(filterStatus) ||
               // Handle case where status might be in different format
               (filterStatus === 'completed' && invoiceStatus === 'completed') ||
               (filterStatus === 'cancelled' && invoiceStatus.includes('cancel')) ||
               (filterStatus === 'pending' && (invoiceStatus === 'pending' || invoiceStatus === 'draft')) ||
               (filterStatus === 'outstanding' && (invoiceStatus === 'outstanding' || invoiceStatus.includes('due')));
      });
    }
    
    // Ensure we have the filtered results before showing any alerts
    setTimeout(() => {
      setSearchResults(filtered);
      
      // Only show the "no results" popup after a small delay to ensure the UI has updated
      setTimeout(() => {
        setLoaderModal(false);
        
        if (filtered.length === 0) {
          swal({ 
            title: "Information", 
            text: "No invoices found matching your filters", 
            icon: "info", 
            buttons: false, 
            timer: 2000 
          });
        }
      }, 300);
    }, 300);
  };
  

  const handleNameSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      applyFilters();
    }
  };

  const handleAddNewInvoice = () => {
    history.push('/addInvoice');
  };

  const handleClearFilter = () => {
    setNameSearchTerm('');
    setInvoiceNumberFilter('');
    setStatusFilter('');
    fetchAllInvoices();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Enter key press handler for search
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Enter key press handler for invoice search
  const handleInvoiceKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleInvoiceNumberSearch();
    }
  };

  return (
    <div className="d-flex" id="wrapper">
      <Side />
      <div id="content-wrapper" className="d-flex flex-column">
        <div id="content">
          <Top />
          <div className="container-fluid">
            <div className="app-content">
              <section className="section">
                <div className="row">
                  <Col lg='12'>
                    <Tab.Container>
                      <Row>
                        <Col lg='12'>
                          <div id="collapseDraft" className="card border-0 pb-0 collapse show" data-parent="#accordion">
                            <div className="card-header flex-wrap">
                              <h3 className="fs-18 mb-4 header_title text-black">
                                <Link to="/Dashboard" className="btn mr-2 btn-light">
                                  <FaArrowLeft className='fs-18' size='2.0rem' style={{verticalAlign: 'baseline'}}/>
                                </Link> 
                                Invoice Management
                              </h3>
                              <Button 
                                variant="secondary" 
                                className="float-right btn-primary"
                                onClick={handleAddNewInvoice}
                              >
                                <FaPlus className='mr-2' style={{verticalAlign: "text-top"}} />Add New Invoice
                              </Button>
                            </div>
                            <div className="card-body p-3">
                              {/* <Row>
                                <Col md={4}>
                                  <div className="input-group mb-3">
                                    <input 
                                      type="text" 
                                      className="form-control" 
                                      placeholder="Search Patient"
                                      value={patientSearch}
                                      onChange={(e) => setPatientSearch(e.target.value)}
                                      onKeyPress={handleKeyPress}
                                    />
                                    <div className="input-group-append">
                                      <Button 
                                        variant="outline-primary" 
                                        onClick={handleSearch}
                                      >
                                        <FaSearch />
                                      </Button>
                                    </div>
                                  </div>    
                                </Col>
                              </Row> */}
                              <Row className="mb-4">
                                <Col md={4}>
                                  <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="Search by Name, UHID or Mobile Number"
                                    value={nameSearchTerm}
                                    onChange={(e) => {
                                      setNameSearchTerm(e.target.value);
                                      // No automatic search when typing, just update the state
                                    }}
                                    onKeyPress={handleNameSearchKeyPress}
                                  />
                                </Col>
                                <Col>
                                  <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="By Invoice Number"
                                    value={invoiceNumberFilter}
                                    onChange={(e) => {
                                      setInvoiceNumberFilter(e.target.value);
                                      // No automatic search when typing, just update the state
                                    }}
                                    onKeyPress={handleFilterKeyPress}
                                  />
                                </Col>
                                <Col md={1.5}>
                                  <select 
                                    className="form-control"
                                    value={statusFilter}
                                    onChange={(e) => {
                                      // Just update the state, don't trigger search automatically
                                      setStatusFilter(e.target.value);
                                    }}
                                  >
                                    <option value="">Status</option>
                                    <option value="Cancelled">Cancelled</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Full Discounted">Full Discounted</option>
                                    <option value="Outstanding">Outstanding</option>
                                    <option value="Pending">Pending</option>
                                  </select>
                                </Col>
                                <Col>
                                  <Button 
                                    variant="outline-primary" 
                                    onClick={applyFilters}
                                    className="mr-2"
                                  >
                                    <FaSearch /> Search
                                  </Button>
                                  <Button 
                                    variant="outline-secondary" 
                                    onClick={handleClearFilter}
                                  >
                                    Clear
                                  </Button>
                                </Col>
                              </Row>
                              <div className="mb-3">
                                <ul className="nav nav-tabs">
                                  <li className="nav-item">
                                    <a 
                                      className={`nav-link ${activeTab === 'invoices' ? 'active' : ''}`} 
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleTabChange('invoices');
                                      }}
                                    >
                                      Invoices
                                    </a>
                                  </li>
                                  <li className="nav-item">
                                    <a 
                                      className={`nav-link ${activeTab === 'vendorOrders' ? 'active' : ''}`} 
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleTabChange('vendorOrders');
                                      }}
                                    >
                                      Vendor Orders
                                    </a>
                                  </li>
                                </ul>
                              </div>

                              {activeTab === 'invoices' && (
                                <div className="table-responsive">
                                  <Table hover>
                                    <thead style={{ backgroundColor: '#7fd1ba6b' }}>
                                      <tr>
                                        <th>Invoice Date</th>
                                        <th>Invoice Number</th>
                                        <th>Patient Name</th>
                                        <th>Department</th>
                                        <th>Status</th>
                                        <th>Net Amount</th>
                                        <th>Due</th>
                                        <th>Discount</th>
                                        <th>Received</th>
                                        <th>Refund</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {searchResults.length === 0 ? (
                                        <tr>
                                          <td colSpan="11" className="text-center">No invoices found</td>
                                        </tr>
                                      ) : (
                                        searchResults.map((invoice, index) => {
                                          // Get the base values from the invoice
                                          const netAmount = parseFloat(invoice.netAmount || 0);
                                          const received = parseFloat(invoice.received || 0);
                                          
                                          // Calculate global discount
                                          let globalDiscount = 0;
                                          
                                          // Check for percentage-based global discount
                                          if (invoice.invoicePType === "Percentage(%)" && invoice.Dpercentag) {
                                            globalDiscount = (netAmount * parseFloat(invoice.Dpercentag)) / 100;
                                          } 
                                          // Check for fixed amount global discount
                                          else if (invoice.invoiceRType === "Fixed amount" && invoice.invoiceR) {
                                            globalDiscount = parseFloat(invoice.invoiceR);
                                          }
                                          // Fall back to standard discount field
                                          else if (invoice.discount) {
                                            globalDiscount = parseFloat(invoice.discount || 0);
                                          }
                                          
                                          // Get item-specific total discount
                                          // Note: The API response shows this field might be capitalized differently in various records
                                          let itemDiscountsTotal = 0;
                                          
                                          // Check for both possible field name variations in the response
                                          if (invoice.invoiceTotalDis && !isNaN(parseFloat(invoice.invoiceTotalDis))) {
                                            itemDiscountsTotal = parseFloat(invoice.invoiceTotalDis);
                                          } else if (invoice.InvoiceTotalDis && !isNaN(parseFloat(invoice.InvoiceTotalDis))) {
                                            itemDiscountsTotal = parseFloat(invoice.InvoiceTotalDis);
                                          }
                                          
                                          // Calculate the combined total discount
                                          const totalCombinedDiscount = globalDiscount + itemDiscountsTotal;
                                          
                                          // Calculate due amount correctly (net amount minus received amount)
                                          const due = Math.max(0, netAmount - received);
                                          
                                          // Determine status with proper handling of different formats
                                          let status = invoice.status || "Pending";
                                          if (status.toLowerCase() === "completed" || status.toLowerCase() === "success" || status.toLowerCase() === "complete") {
                                            status = "Completed";
                                          } else if (due <= 0) {
                                            status = "Paid";
                                          } else if (due < netAmount) {
                                            status = "Partially Paid";
                                          }
                                          
                                          return (
                                            <tr key={invoice.invoiceId || index}>
                                              <td>{formatDate(invoice.invoiceDate)}</td>
                                              <td>
                                                <Link 
                                                  to={`/viewInvoice/${invoice.invoiceNumber}`} 
                                                  style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                                                >
                                                  {invoice.invoiceNumber}
                                                </Link>
                                              </td>
                                              <td>{invoice.patientName}</td>
                                              <td>{invoice.department}</td>
                                              <td>{status}</td>
                                              <td>₹{netAmount.toFixed(2)}</td>
                                              <td>₹{due.toFixed(2)}</td>
                                              <td>₹{totalCombinedDiscount.toFixed(2)}</td>
                                              <td>₹{received.toFixed(2)}</td>
                                              <td>₹{(parseFloat(invoice.refund || 0)).toFixed(2)}</td>
                                              <td>
                                                <button 
                                                  onClick={() => handlePrint(invoice)} 
                                                  className="btn btn-sm btn-info mr-1"
                                                >
                                                  <FaPrint /> Print
                                                </button>
                                                {status.toLowerCase() !== "completed" && (
                                                  <Link to={`/editInvoice/${invoice.invoiceNumber}`} className="btn btn-sm btn-primary mr-1">
                                                    <FaEdit /> Edit
                                                  </Link>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              )}

                              {activeTab === 'vendorOrders' && (
                                <div className="table-responsive">
                                  <Table hover>
                                    <thead style={{ backgroundColor: '#7fd1ba6b' }}>
                                      <tr>
                                        <th>Invoice Date</th>
                                        <th>Invoice Number</th>
                                        <th>Patient Name</th>
                                        <th>UHID</th>
                                        <th>Vendor Name</th>
                                        <th>Item Names</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td colSpan="6" className="text-center">No vendor orders found</td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                </div>
                              )}

                              <div className="d-flex justify-content-between align-items-center mt-3">
                                <div>
                                  Rows per page: 
                                  <select className="ml-2">
                                    <option>10</option>
                                    <option>20</option>
                                    <option>50</option>
                                  </select>
                                </div>
                                <div>
                                  <span>1-{Math.min(searchResults.length, 10)} of {searchResults.length}</span>
                                  <button className="btn btn-sm ml-2" disabled={true}>&lt;</button>
                                  <button className="btn btn-sm" disabled={searchResults.length <= 10}>&gt;</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Tab.Container>
                  </Col>
                </div>
              </section>
            </div>
          </div>
          <Loader show={loaderModal} />
        </div>
      </div>
    </div>
  );
};

export default Invoice;